// React import not needed in React 17+
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { CircularProgress, Box, Alert } from '@mui/material';
import { securityConfig } from '../../security/config';

type RoleType = string | string[];
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: RoleType;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole
}) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  // Define enterprise paths
  const enterprisePaths = [
    '/enterprise/vcn',
    '/enterprise/iso8583-guard',
    '/enterprise/gateway-sniffer'
  ];

  const isEnterprisePath = enterprisePaths.some(path => location.pathname.startsWith(path));

  // Add debugging to help identify the issue
  if (process.env.NODE_ENV === 'development') {
    console.log('[ProtectedRoute] Debug info:', {
      isAuthenticated,
      user: user ? {
        userId: (user as any)?.userId,
        role: (user as any)?.role,
        entity_type: (user as any)?.entity_type,
        userType: typeof (user as any)?.role
      } : null,
      requiredRole,
      pathname: location.pathname,
      loading
    });
  }

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Alert severity="info">
          Checking authentication status...
        </Alert>
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Check if it's an enterprise path AND auth is not enforced for enterprise
    if (isEnterprisePath && !securityConfig.enforceAuthForEnterprise) {
      // Allow access for development/testing
      return <>{children}</>;
    }
    // Otherwise, redirect to landing page
    return <Navigate to="/" replace />;
  }

  // Only check role for USER entity_type
  // Defensive: check for entity_type on user (for legacy User type)
  if (requiredRole && user && (user as any)?.entity_type === 'USER') {
    const userRole = (user as any)?.role;
    
    // Safety check: ensure userRole is a string or can be converted to string
    if (userRole && typeof userRole !== 'string' && typeof userRole !== 'undefined') {
      console.error('[ProtectedRoute] User role is not a string:', userRole, typeof userRole);
      return <Navigate to="/" replace />;
    }

    // Convert userRole to string if it exists
    const safeUserRole = userRole ? String(userRole) : '';
    
    if (Array.isArray(requiredRole)) {
      // Ensure all required roles are strings
      const safeRequiredRoles = requiredRole.map(role => String(role));
      if (!safeRequiredRoles.includes(safeUserRole)) {
        return <Navigate to="/" replace />;
      }
    } else {
      // Ensure single required role is string
      const safeRequiredRole = String(requiredRole);
      if (safeUserRole !== safeRequiredRole) {
        return <Navigate to="/" replace />;
      }
    }
  }

  return <>{children}</>;
};